
import React from 'react';
import { PollOption } from '@/types/poll';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Check, ThumbsUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PollOptionsProps {
  options: PollOption[];
  onVote: (optionId: number) => void;
  selectedId?: number;
  hasVoted: boolean;
  canVote: boolean;
  totalVotes: number;
  variant?: 'default' | 'compact';
}

const PollOptions = ({
  options,
  onVote,
  selectedId,
  hasVoted,
  canVote,
  totalVotes,
  variant = 'default'
}: PollOptionsProps) => {
  const isCompact = variant === 'compact';
  
  // Function to calculate percentage
  const calculatePercentage = (option: PollOption): number => {
    // Use pre-calculated percentage if available
    if (typeof option.percentage === 'number') {
      return option.percentage;
    }
    
    // Fall back to calculating it
    if (totalVotes === 0) return 0;
    return Math.round((option.votes / totalVotes) * 100);
  };

  // Function to determine progress bar color
  const getProgressColor = (percentage: number) => {
    if (percentage >= 75) return "var(--bg-green-500)";
    if (percentage >= 50) return "var(--bg-green-400)";
    if (percentage >= 25) return "var(--bg-amber-400)";
    return "var(--bg-red-400)";
  };

  return (
    <div className={`space-y-3 w-full ${isCompact ? 'text-sm' : ''}`}>
      {options.map(option => {
        const isSelected = selectedId === option.id;
        const percentage = calculatePercentage(option);
        const hasSentiment = typeof option.sentiment === 'number';
        
        // Always show the results view for all options
        return (
          <div key={option.id} className="space-y-1 w-full">
            <div 
              className={cn(
                "relative p-2 sm:p-3 rounded-md border cursor-pointer w-full overflow-hidden poll-option-container",
                isSelected ? "border-nation-blue poll-option-selected" : "border-gray-200 poll-option-default dark:border-gray-700 dark:bg-gray-800/50",
                !hasVoted && canVote ? "hover:border-nation-blue poll-option-hoverable dark:hover:bg-gray-700/70" : ""
              )}
              onClick={() => canVote && !hasVoted && onVote(option.id)}
              role={canVote && !hasVoted ? "button" : undefined}
              data-poll-option
            >
              <div className="flex justify-between mb-1 gap-2 w-full">
                <div className="flex items-center min-w-0 flex-1">
                  {isSelected && (
                    <Check size={16} className="text-nation-blue mr-1 flex-shrink-0" />
                  )}
                  <span className={cn("break-words line-clamp-2 min-w-0 poll-option-text", isSelected ? "font-medium" : "")}>{option.text}</span>
                  
                  {hasSentiment && (
                    <span className={cn(
                      "ml-2 text-xs px-1.5 py-0.5 rounded flex items-center flex-shrink-0 whitespace-nowrap sentiment-badge",
                      option.sentiment! > 0 ? "sentiment-positive" : 
                      option.sentiment! < 0 ? "sentiment-negative" : 
                      "sentiment-neutral"
                    )}>
                      <ThumbsUp 
                        size={12} 
                        className={option.sentiment! >= 0 ? "" : "rotate-180"} 
                      />
                      <span className="ml-1">
                        {option.sentiment! > 0 ? '+' : ''}{option.sentiment}
                      </span>
                    </span>
                  )}
                </div>
                <span className="font-medium flex-shrink-0 text-sm sm:text-base">{percentage}%</span>
              </div>
              
              <Progress 
                value={percentage} 
                className="h-2 w-full"
                style={{
                  "--progress-indicator-color": getProgressColor(percentage),
                } as React.CSSProperties}  
              />
              
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {option.votes} {option.votes === 1 ? 'vote' : 'votes'}
              </div>
            </div>
          </div>
        );
      })}
      
      <p className="text-sm text-muted-foreground mt-1">
        Total responses: {totalVotes}
      </p>
    </div>
  );
};

export default PollOptions;
